import { LitElement, css, html } from 'lit';
import { OrganizationPurchaseHistory, PurchaseRecords, PurchaseSummaryResponse } from '../types';
import { customElement, property, state } from 'lit/decorators';

import { formatPrice } from '../helpers/api/products-api';
import { isDefined } from '../helpers/utils';

@customElement('completed-purchases-component')
export class CompletedPurchasesComponent extends LitElement {

    @property({ type: Object })
    data?: OrganizationPurchaseHistory;

    @state()
    sortBy: string = '';

    @state()
    sortOrder: 'asc' | 'desc' = 'asc';

    @state()
    selectedTab: number | null = 0;

    @state()
    purchases: PurchaseSummaryResponse[] = [];

    amplitude_callsite: string = 'Completed Purchases Component';

    options = ['Completed', 'Pending/Abandoned (Coming Soon)'];

    async updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {
            if(propName === 'data') {
                // Sort using string comparison
                this.purchases = this.data?.transactions?.sort((a, b) => {
                    const dateA = isDefined(a.payment_complete_at) ? new Date(a.payment_complete_at) : new Date(0);
                    const dateB = isDefined(b.payment_complete_at) ? new Date(b.payment_complete_at) : new Date(0);
                    return dateB.getTime() - dateA.getTime();
                }) ?? [];
            }
        });
    }

    private sortClickHandler(sortBy: string) {
        if (this.sortBy === sortBy) {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortBy = sortBy;
            this.sortOrder = 'asc';
        }

        this.purchases = this.purchases.sort((a, b) => {
            const comparison = 0;
            if (sortBy === 'value') {
                return a.total_price_cents - b.total_price_cents;
            } else if (sortBy === 'user') {
                return a.user_email.localeCompare(b.user_email);
            } else if (sortBy === 'date') {
                const dateA = isDefined(a.payment_complete_at) ? new Date(a.payment_complete_at) : new Date(0);
                const dateB = isDefined(b.payment_complete_at) ? new Date(b.payment_complete_at) : new Date(0);
                return dateA.getTime() - dateB.getTime();
            }

            return this.sortOrder === 'asc' ? comparison : -comparison;
        });
    }

    getRecordType(purchase_records: PurchaseRecords | undefined): string {
        if (!purchase_records) {
            return 'PURCHASE';
        }
        if ('Form' in purchase_records) {
            return 'SUBMISSION';
        } else if ('ActivatedChallenge' in purchase_records) {
            return 'ACTIVATED CHALLENGE';
        } else if ('Paywall' in purchase_records) {
            return 'PAYWALL';
        } else {
            return 'PURCHASE';
        }
    }

    private isSubscription(purchase: PurchaseSummaryResponse): boolean {
        const hasRecurringItem = purchase.purchase_items?.some(item => isDefined(item.recurring_interval));
        return hasRecurringItem || isDefined(purchase.subscription_id);
    }

    private getRecurringText(purchase: PurchaseSummaryResponse): string {
        const recurringInterval = purchase.purchase_items?.find(item => isDefined(item.recurring_interval))?.recurring_interval;
        if (!recurringInterval) return 'Recurring';
        switch (recurringInterval) {
            case 'weekly':
                return 'Billed weekly';
            case 'yearly':
                return 'Billed yearly';
            default:
                return 'Billed monthly';
        }
    }

    render() {
        return html`
            <div class="innerItem flexContainer">
                <div class="editWrapper">
                    <span class="formLabel">Revenue Summary</span>
                    <span>Total revenue from feature-based purchases.</span>

                    <div class="revenue-summary">
                        <div class="revenue-card">
                            <div class="revenue-amount">
                                ${this.data?.total_revenue_cents
                                    ? this.data?.total_revenue_cents.map(c => formatPrice(c.revenue_cents, c.currency)).join(', ')
                                    : 'No revenue data available'
                                }
                            </div>
                            <div class="revenue-label">Total Revenue</div>
                        </div>
                        <div class="revenue-card">
                            <div class="revenue-amount">
                                ${this.data?.total_revenue_cents
                                    ? this.data?.total_revenue_cents.map(c => formatPrice(c.earned_cents, c.currency)).join(', ')
                                    : 'No revenue data available'
                                }
                            </div>
                            <div class="revenue-label">Net Earned</div>
                        </div>
                        <div class="revenue-card">
                            <div class="revenue-amount">
                                ${this.data?.transactions.length ?? 0}
                            </div>
                            <div class="revenue-label">Total Transactions</div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Transaction History -->
            <div class="innerItem flexContainer">
                <div class="editWrapper">
                    <span class="formLabel">Transactions</span>
                    <span>History of feature-based purchases and payments.</span>
                    <div class="purchases-wrapper">
                        <div class="header-row">
                            <div class="header-cell text" @click="${() => this.sortClickHandler('user')}">User ${this.sortBy === 'user' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell number" @click="${() => this.sortClickHandler('date')}">Date ${this.sortBy === 'date' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell number" @click="${() => this.sortClickHandler('feature')}">Feature ${this.sortBy === 'feature' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell number" @click="${() => this.sortClickHandler('value')}">Amount ${this.sortBy === 'value' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell number">Provider</div>
                            <div class="header-cell number">Status</div>
                            <div class="header-cell number">Actions</div>
                        </div>
                        ${this.purchases.map((purchase) => html`
                            <div class="challenge-row">
                                <div class="data-cell text">
                                    ${purchase.user_email}
                                </div>
                                <div class="data-cell number">
                                    ${this.formatDate(purchase.payment_complete_at ?? '')}
                                </div>
                                <div class="data-cell number">
                                    <div class="feature-cell">
                                        <span>${this.getRecordType(purchase.purchase_records)}</span>
                                        ${this.isSubscription(purchase) ? html`<span class="subscription-pill">Subscription</span>` : ''}
                                    </div>
                                </div>
                                <div class="data-cell number">
                                    ${this.isSubscription(purchase) ? html`<span>${this.getRecurringText(purchase)}</span>` : ''}
                                    ${!this.isSubscription(purchase) ? formatPrice(purchase.total_price_cents, purchase.currency) : ''}
                                    ${isDefined(purchase.refund_amount_cents) ? html`<br><span style="color: red;">(Refunded: ${formatPrice(purchase.refund_amount_cents, purchase.refund_currency ?? purchase.currency)})</span>` : ''}
                                </div>
                                <div class="data-cell number">
                                    <span class="provider-badge ${purchase.payment_provider}">
                                        ${purchase.payment_provider === 'stripe' ? 'Stripe' : 'Proxi'}
                                    </span>
                                </div>
                                <div class="data-cell number">
                                    <span class="${this.getStatusBadgeClass(purchase.status)}">
                                        ${purchase.status.charAt(0).toUpperCase() + purchase.status.slice(1)}
                                    </span>
                                </div>
                                <div class="data-cell number">
                                    <a href="${purchase.receipt_url}" target="_blank">View Receipt</a>
                                </div>
                            </div>
                        `)}
                    </div>
                </div>
            </div>
        `;
    }

    private getStatusBadgeClass(status: string): string {
        switch (status) {
            case 'completed':
            case 'active':
                return 'status-badge status-success';
            case 'pending':
                return 'status-badge status-warning';
            case 'failed':
            case 'restricted':
                return 'status-badge status-error';
            default:
                return 'status-badge status-neutral';
        }
    }

    private formatDate(dateString: string): string {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    static get styles() {
        return css`
            :host {
                --modal-width: 800px;
                font-size: 12px;
                display: flex;
                flex-direction: column;
            }

            .modalContent {
                position: relative;
                width: 100%;
                max-height: calc(80vh - 180px);
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
            }

             .editWrapper {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                width: 100%;
            }

            .labelColumn {
                width: 100%;
                max-width: 21rem;
                flex:1;
            }

            .editSection {
                overflow: hidden;
                padding-top: 0.5rem;
                margin-bottom: 0.5rem;
                transition: max-height 0.5s ease;
                flex: 1;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 1rem;
            }


            .modal__header {
                width: 100%;
                text-align: center;
                padding: 0px 20px 5px;
                color: #000000;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 20px 0 5px;
                width: 100%;
                box-sizing: border-box;
            }

            .header__actions {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-end;
                gap: 10px;
                box-sizing: border-box;
            }


            .formLabel {
                color: var(--theme-input-color);
                font-size: 0.75rem;
                font-weight: bold;
                display: block;
            }

            .formValue {
                color: var(--theme-input-color);
                font-size: 1rem;
                word-break: break-all;
            }

            .header-row {
                cursor: pointer;
                width: 100%;
                align-items: center;
                font-weight: 700;
                display: flex;
                height: 40px;
                background-color: var(--theme-border-color);
                gap: 10px;
            }

            .header-row mwc-icon {
                font-size: 20px;
            }

            .header-cell,
            .data-cell {
                align-items: center;
            }

            .feature-cell {
                display: flex;
                flex-direction: column;
                gap: 4px;
                align-items: center;
            }

            .header-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .header-cell.text {
                text-align: left;
                width: 35%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .header-cell.channel {
                text-align: center;
                width: 15%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .purchases-wrapper {
                padding-top: 10px;
                display: flex;
                flex-direction: column;
                width: 100%;
                box-sizing: border-box;
                margin-top: 10px;
            }

            .header-cell.number {
                text-align: center;
                width: 15%;
                justify-content: center;
            }

            .header-cell.primary {
                flex: 1;
            }

            .data-cell {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 0 10px;
            }

            .data-cell.text {
                text-align: left;
                width: 35%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .data-cell.number {
                text-align: center;
                width: 15%;
                justify-content: center;
            }

            .data-cell.primary {
                flex: 1;
            }

            .provider-badge {
                padding: 0.125rem 0.5rem;
                border-radius: 0.25rem;
                font-size: 0.75rem;
                font-weight: 500;
                text-transform: uppercase;
            }

            .provider-badge.stripe {
                background-color: #635bff20;
                color: #635bff;
            }

            .provider-badge.chargebee {
                background-color: #ff702820;
                color: #ff7028;
            }

            .innerItem {
                border-top: 1px solid var(--theme-border-color);
                padding-top: 1rem;
                padding-bottom: 1rem;
                flex: 1;
                gap: 20px;
            }

            .revenue-summary {
                margin-top: 1rem;
                display: flex;
                gap: 1rem;
            }

            .revenue-card {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: .5rem;
                border: 1px solid var(--theme-border-color);
                border-radius: 0.5rem;
                min-width: 100px;
            }

            .revenue-amount {
                font-size: 1.25rem;
                font-weight: 600;
                color: var(--main-primary-color);
                margin-bottom: 0.5rem;
            }

            .revenue-label {
                font-size: 0.7rem;
                color: var(--theme-light-text-color);
                text-align: center;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                --mdc-typography-button-text-transform: capitalize;
            }

            .aqua-blue {
                --mdc-theme-primary: var(--main-secondary-color);
            }

            .challenges-wrapper {
                width: 100%;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
            }

            .challenge-row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 5px;
                box-sizing: border-box;
                border-bottom: 1px solid #DADADA;
                gap: 10px;
                padding-top: 12px;
            }

            .challenge-row:hover {
                background-color: var(--theme-border-color);
                cursor: context-menu;
            }

            .subscription-pill {
                background: #e0f2fe;
                color: #0ea5e9;
                font-weight: 600;
                font-size: 0.7rem;
                padding: 2px 6px;
                border-radius: 999px;
            }

            .expanded-row {
                width: 100%;
                background: #f9fafb;
                border-radius: 6px;
                padding: 8px 10px;
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                gap: 6px;
                margin-top: 8px;
            }

            .expanded-line {
                display: flex;
                justify-content: space-between;
                gap: 8px;
                font-size: 0.9rem;
                color: #102C3D;
            }

            .expanded-line .label {
                color: #6b7280;
            }

            .item-line {
                display: block;
                font-size: 0.85rem;
                color: #374151;
            }

            .user {
                color: #102C3D;
                text-align: left;
                line-height: 125%;
            }

            .stats {
                box-sizing: border-box;
                color: #102C3D;
                text-align: center;
                line-height: 125%;
            }

            .view-more {
                cursor: pointer;
                color: #102C3D;
                text-align: right;
                font-weight: 700;
                line-height: 125%;
                text-decoration-line: underline;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}
